import React, { useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  PolylineF,
  MarkerF,
  InfoWindowF
} from "@react-google-maps/api";
import polyline from "@mapbox/polyline";
import "./RouteList.css";

const style = {
  width: "100%",
  height: "400px",
};

export default function RouteMap({ routeData }) {
  const [visibleMarkers, setVisibleMarkers] = useState({});

  const openMarker = (key) => {
    setVisibleMarkers(prev => ({ ...prev, [key]: true }));
  };

  const closeMarker = (key) => {
    setVisibleMarkers(prev => ({ ...prev, [key]: false }));
  };

  const truncate = (text, max = 18) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max) + "..." : text;
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // Decode polyline from backend
  const path = routeData?.polyline
    ? polyline
      .decode(routeData.polyline.trim())
      .map(([lat, lng]) => ({ lat, lng }))
    : [];


  // Parse origin
  let origin = null;
  if (routeData?.origin) {
    const [lat, lng] = routeData.origin.split(",");
    origin = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };
  }


  // Parse destination
  let destination = null;
  if (routeData?.destination) {
    const [lat, lng] = routeData.destination.split(",");
    destination = {
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    };
  }


  // Parse waypoints
  let waypoints = [];
  if (routeData?.waypoints) {
    waypoints = routeData.waypoints.map((wp) => {
      const [lat, lng] = wp.split(",");
      return {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
      };
    });
  }

  const getInitials = (text) => {
    if (!text) return "";
    return text
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase();
  };

  if (!isLoaded || !routeData) {
    return <p>Loading map…</p>;
  }

  return (
    <GoogleMap
      key={routeData?.polyline}
      mapContainerStyle={style}
      center={origin || { lat: 20.5937, lng: 78.9629 }}
      zoom={10}
    >
      {/* Draw route line */}
      {path.length > 0 && (
        <PolylineF
          path={path}
          options={{
            strokeColor: "#1976D2",
            strokeWeight: 5,
          }}
        />
      )}

      {/* Origin Marker */}
      {/* {origin && <MarkerF position={origin} label="O" />} */}
      {origin && (
        <>
          <MarkerF
            position={origin}
            label={{
              text: getInitials(routeData.origin_name || "Origin"),
              color: "#000",
              fontWeight: "bold",
              fontSize: "12px",
            }}
            onClick={() => openMarker("origin")}
          />

          {visibleMarkers["origin"] && (
            <InfoWindowF
              position={origin}
              onCloseClick={() => closeMarker("origin")}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#333",
                whiteSpace: "nowrap",
              }}>
                <span title={routeData.origin_name}>
                  {truncate(routeData.origin_name || "Origin")}
                </span>
                <span
                  onClick={() => closeMarker("origin")}
                  style={{
                    cursor: "pointer",
                    color: "#aaa",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    padding: "0 2px",
                  }}
                >
                  ✕
                </span>
              </div>
            </InfoWindowF>
          )}
        </>
      )}

      {/* Waypoint Markers */}
      {/* {waypoints.map((wp, index) => (
        <MarkerF key={index} position={wp} label={`${index + 1}`} />
      ))} */}

      {waypoints.map((wp, index) => {
        const key = `wp-${index}`;
        const name = routeData.waypoint_names?.[index] || `Stop ${index + 1}`;

        return (
          <React.Fragment key={key}>
            <MarkerF
              position={wp}
              label={{
                text: getInitials(name),
                color: "#000",
                fontWeight: "bold",
                fontSize: "12px",
              }}
              onClick={() => openMarker(key)}
            />

            {visibleMarkers[key] && (
              <InfoWindowF
                position={wp}
                onCloseClick={() => closeMarker(key)}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#333",
                  whiteSpace: "nowrap",
                }}>
                  <span title={name}>
                    {truncate(name)}
                  </span>
                  <span
                    onClick={() => closeMarker(key)}
                    style={{
                      cursor: "pointer",
                      color: "#aaa",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      padding: "0 2px",
                    }}
                  >
                    ✕
                  </span>
                </div>
              </InfoWindowF>
            )}
          </React.Fragment>
        );
      })}

      {/* Destination Marker */}
      {/* {destination && <MarkerF position={destination} label="D" />} */}
      {destination && (
        <>
          <MarkerF
            position={destination}
            label={{
              text: getInitials(routeData.destination_name || "Destination"),
              color: "#000",
              fontWeight: "bold",
              fontSize: "12px",
            }}
            onClick={() => openMarker("destination")}
          />

          {visibleMarkers["destination"] && (
            <InfoWindowF
              position={destination}
              onCloseClick={() => closeMarker("destination")}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#333",
                whiteSpace: "nowrap",
              }}>
                <span title={routeData.destination_name}>
                  {truncate(routeData.destination_name || "Destination")}
                </span>
                <span
                  onClick={() => closeMarker("destination")}
                  style={{
                    cursor: "pointer",
                    color: "#aaa",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    padding: "0 2px",
                  }}
                >
                  ✕
                </span>
              </div>
            </InfoWindowF>
          )}
        </>
      )}
    
    </GoogleMap>
  );
}
